"use client";

import React, { useState, useEffect } from "react";
import { PrismicImage, SliceZone } from "@prismicio/react";
import { components } from "@/slices";
import Link from "next/link";

export default function CHeader({ logo, slices, context }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(true);

  const openMenu = () => {
    setShouldAnimate(true);
    setIsMenuOpen(true);
  };

  const closeMenu = () => {
    setShouldAnimate(true);
    setIsMenuOpen(false);
  };

  const closeMenuInstant = () => {
    setShouldAnimate(false);
    setIsMenuOpen(false);
  };

  // Block scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  // Extend context to include closeMenuInstant function for navigation items
  const extendedContext = {
    ...context,
    closeMenu: closeMenuInstant
  };

  return (
    <header className={`w-full px-5 lg:px-20 3xl:!px-40 pt-3 md:pt-6 w-full absolute top-0 left-0 text-moyam-orange transition-all z-140 ${isMenuOpen ? 'z-300' : 'z-140'}`}>
      <div className="flex flex-row justify-between items-center w-full box-border py-4 pr-4 md:pr-10 pl-[22px] md:pl-[56px] bg-beige/60 md:bg-beige/70 backdrop-blur-2xl rounded-2xl md:rounded-full">
        <Link href="/">
          <PrismicImage field={logo} className="h-[18px] md:h-[44px]" />
        </Link>
        <ul className="hidden md:flex flex-row justify-start items-center gap-6 list-none m-0 p-0">
          <SliceZone slices={slices} components={components} context={context} />
        </ul>
        <button 
          onClick={openMenu}
          className="md:hidden bg-transparent border-0 p-0 cursor-pointer"
          aria-label="Ouvrir le menu">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15.625 7.125C15.625 7.42337 15.5262 7.70952 15.3504 7.92049C15.1746 8.13147 14.9361 8.25 14.6875 8.25H0.9375C0.68886 8.25 0.450403 8.13147 0.274587 7.92049C0.0987721 7.70952 0 7.42337 0 7.125C0 6.82663 0.0987721 6.54048 0.274587 6.3295C0.450403 6.11853 0.68886 6 0.9375 6H14.6875C14.9361 6 15.1746 6.11853 15.3504 6.3295C15.5262 6.54048 15.625 6.82663 15.625 7.125ZM0.9375 2.25H14.6875C14.9361 2.25 15.1746 2.13147 15.3504 1.9205C15.5262 1.70952 15.625 1.42337 15.625 1.125C15.625 0.826631 15.5262 0.540483 15.3504 0.329505C15.1746 0.118526 14.9361 0 14.6875 0H0.9375C0.68886 0 0.450403 0.118526 0.274587 0.329505C0.0987721 0.540483 0 0.826631 0 1.125C0 1.42337 0.0987721 1.70952 0.274587 1.9205C0.450403 2.13147 0.68886 2.25 0.9375 2.25ZM14.6875 12H0.9375C0.68886 12 0.450403 12.1185 0.274587 12.3295C0.0987721 12.5405 0 12.8266 0 13.125C0 13.4234 0.0987721 13.7095 0.274587 13.9205C0.450403 14.1315 0.68886 14.25 0.9375 14.25H14.6875C14.9361 14.25 15.1746 14.1315 15.3504 13.9205C15.5262 13.7095 15.625 13.4234 15.625 13.125C15.625 12.8266 15.5262 12.5405 15.3504 12.3295C15.1746 12.1185 14.9361 12 14.6875 12Z" fill="#E94F2F" />
          </svg>
        </button>
      </div>
      
      {/* Overlay */}
      {isMenuOpen && (
        <div 
          className={`md:hidden fixed inset-0 bg-black/30 z-140 ${shouldAnimate ? 'transition-opacity duration-300 ease-in-out' : ''}`}
          onClick={closeMenu}
          aria-label="Fermer le menu"
        />
      )}

      {/* Mobile Menu */}
      <div className={`md:hidden w-full bg-beige flex flex-col fixed top-0 left-0 z-200 px-4 pt-10 pb-12 gap-10 ${shouldAnimate ? 'transition-all duration-300 ease-in-out' : ''} ${isMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}`}>
        <div className="flex flex-row justify-between items-center w-full box-border">
          <Link href="/" onClick={closeMenu}>
            <PrismicImage field={logo} className="h-[18px]" />
          </Link>
          <button 
            onClick={closeMenu}
            className="bg-transparent border-0 p-0 cursor-pointer"
            aria-label="Fermer le menu">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.7656 14.6336C15.8399 14.708 15.8988 14.7962 15.9391 14.8933C15.9793 14.9904 16 15.0945 16 15.1996C16 15.3047 15.9793 15.4088 15.9391 15.5059C15.8988 15.603 15.8399 15.6912 15.7656 15.7656C15.6912 15.8399 15.603 15.8988 15.5059 15.9391C15.4088 15.9793 15.3047 16 15.1996 16C15.0945 16 14.9904 15.9793 14.8933 15.9391C14.7962 15.8988 14.708 15.8399 14.6336 15.7656L8 9.13094L1.36637 15.7656C1.21626 15.9157 1.01268 16 0.8004 16C0.588121 16 0.384536 15.9157 0.234432 15.7656C0.0843276 15.6155 4.18453e-09 15.4119 0 15.1996C-4.18453e-09 14.9873 0.0843276 14.7837 0.234432 14.6336L6.86906 8L0.234432 1.36637C0.0843276 1.21626 -1.5816e-09 1.01268 0 0.8004C1.5816e-09 0.588121 0.0843276 0.384536 0.234432 0.234432C0.384536 0.0843276 0.588121 1.5816e-09 0.8004 0C1.01268 -1.5816e-09 1.21626 0.0843276 1.36637 0.234432L8 6.86906L14.6336 0.234432C14.7837 0.0843276 14.9873 -4.18453e-09 15.1996 0C15.4119 4.18453e-09 15.6155 0.0843276 15.7656 0.234432C15.9157 0.384536 16 0.588121 16 0.8004C16 1.01268 15.9157 1.21626 15.7656 1.36637L9.13094 8L15.7656 14.6336Z" fill="#E94F2F" />
            </svg>
          </button>
        </div>
        <ul className="flex flex-col justify-start items-start gap-6 list-none m-0 p-0">
          <SliceZone slices={slices} components={components} context={extendedContext} />
        </ul>
      </div>
    </header>
  );
}
